import { formatCurrency } from '@/scenes/POS/Core/pos.utils';

/** Non-draft trigger statuses shown in the activated list on trigger index */
export const ACTIVATED_TRIGGER_STATUSES = [
    'SCHEDULED',
    'TRIGGERED',
    'COMPLETED',
    'FAILED',
    'PAUSED',
    'CANCELLED',
] as const;

/** Campaign statuses where the active toggle must be disabled */
export const NON_TOGGLEABLE_CAMPAIGN_STATUSES = ['COMPLETED', 'CANCELLED'] as const;

export function normalizeCampaignStatus(status: string | null | undefined): string {
    return status?.toUpperCase() ?? '';
}

export function isCampaignTriggered(status: string | null | undefined): boolean {
    return normalizeCampaignStatus(status) === 'TRIGGERED';
}

export function isCampaignStatusInList(status: string | null | undefined, statuses: readonly string[]): boolean {
    return statuses.includes(normalizeCampaignStatus(status));
}

export function isCampaignStatusToggleDisabled(
    campaignStatus: string | null | undefined,
    options?: { isUpdating?: boolean },
): boolean {
    if (options?.isUpdating) {
        return true;
    }

    return isCampaignStatusInList(campaignStatus, NON_TOGGLEABLE_CAMPAIGN_STATUSES);
}

export function getCampaignIsActive(campaign: { isActive?: boolean | null }): boolean {
    return campaign.isActive ?? false;
}

/** Show Activate on new triggers and DRAFT; hide once moved to the activated list */
export function canShowActivateTriggerButton(
    campaignId: string | null | undefined,
    campaignStatus: string | null | undefined,
): boolean {
    const normalizedStatus = normalizeCampaignStatus(campaignStatus);

    if (!campaignId || !normalizedStatus) {
        return true;
    }

    if (normalizedStatus === 'DRAFT') {
        return true;
    }

    return !ACTIVATED_TRIGGER_STATUSES.includes(normalizedStatus as (typeof ACTIVATED_TRIGGER_STATUSES)[number]);
}

export function formatCampaignOpenRate(openRate: string | null | undefined): string {
    if (openRate === 'N/A' || openRate === '0.00%') {
        return '0%';
    }

    return openRate ?? '0%';
}

export function getCampaignOpenRateSortValue(openRate: string | null | undefined): number {
    if (openRate === 'N/A' || openRate === '0.00%') {
        return 0;
    }

    return Number(openRate);
}

export function formatCampaignRevenue(revenue: string | number | null | undefined): string {
    return revenue === 'N/A' ? formatCurrency(0) : formatCurrency(revenue);
}

export function getCampaignRevenueSortValue(revenue: string | number | null | undefined): number {
    return revenue === 'N/A' ? 0 : Number(revenue);
}
